import { chartTheme, storageFormatters } from "@jana-technology/platform-ui";
import type { useQuery } from "@apollo/client/react";
import { sortBy } from "lodash-es";

import { REFERENCE_LINE_BASE } from "constants/charts";
import type {
  RiskComparisonConfigInput,
  RiskComparisonChartConfig,
  RiskComparisonPeerWells,
  RiskComparisonWellRanks,
} from "./types";
import { nthNumberFormatter } from "components/tables/utils/formatters";
import { TOOLTIP_SEMIBOLD } from "constants/styles";
import type { RiskUnitFormatter } from "helpers/risk-units";
import type {
  AssetSelectionQuery,
  AssetSelectionQueryVariables,
  RiskManagementOverallComparisonChartQuery,
} from "$/__generated__/graphql";

export const findHierarchyName = (
  hierarchyId: number | null,
  hierarchies: NonNullable<
    NonNullable<
      useQuery.Result<AssetSelectionQuery, AssetSelectionQueryVariables>["data"]
    >["hierarchies"]
  >,
): string => {
  for (const item of hierarchies) {
    if (item.hierarchyId === hierarchyId) {
      return item.name ?? "";
    }
    if (item.items) {
      const name = findHierarchyName(hierarchyId, item.items);
      if (name) {
        return name;
      }
    }
  }
  return "";
};

const splitSelectedAndPeerWells = <T extends { wellName: string }>(
  allWellsSeries: Array<T>,
  wellName: string,
  hierarchyId: number | null | undefined,
  wellNamesByHierarchy: RiskComparisonPeerWells | undefined,
) => {
  const peerWells = wellNamesByHierarchy
    ?.find((hierarchy) => hierarchy?.hierarchyId === hierarchyId)
    ?.wellNames?.filter((name) => name !== wellName);

  const selectedWellSeries = allWellsSeries.filter(
    (datum) => datum.wellName === wellName,
  );

  const otherWellsSeries = peerWells?.length
    ? allWellsSeries.filter((datum) => peerWells.includes(datum.wellName))
    : [];

  return { selectedWellSeries, otherWellsSeries };
};

const buildReferenceLines = (
  fieldMedian: number,
  systemMedian: number,
  theme: typeof chartTheme,
) => [
  {
    ...REFERENCE_LINE_BASE,
    yVal: fieldMedian,
    stroke: theme.colors[0],
    name: "Field Median",
  },
  {
    ...REFERENCE_LINE_BASE,
    yVal: systemMedian,
    stroke: theme.colors[1],
    name: "System Median",
  },
];

export const buildRiskComparisonChartConfig = (
  {
    chartData,
    wellName,
    hierarchyId,
    wellNamesByHierarchy,
    hierarchies,
    theme = chartTheme,
  }: RiskComparisonConfigInput,
  isBaseline: boolean,
  isOverall: boolean,
  riskUnitFormatter?: RiskUnitFormatter,
): RiskComparisonChartConfig => {
  if (!chartData) {
    return {
      series: [],
      referenceLines: [],
      legendDomain: [],
      legendColors: [],
      theme,
    };
  }

  const { wellRanks = [] }: { wellRanks: RiskComparisonWellRanks } = chartData;

  const allWellsSeries = wellRanks.map((well) => {
    const wellHierarchyId = wellNamesByHierarchy?.find((hierarchy) => {
      return hierarchy?.wellNames?.includes(well.wellName);
    })?.hierarchyId;

    const hierarchyName = findHierarchyName(
      wellHierarchyId ?? null,
      hierarchies ?? [],
    );

    return {
      x: well.rank,
      y: isBaseline ? well.operationalRisk : well.interventionRisk,
      label: `${well.wellName} (${hierarchyName})`,
      title: `Risk Rank - ${well.rank}${nthNumberFormatter(well.rank)}`,
      value: `${well.rank} of ${wellRanks.length}`,
      wellName: well.wellName,
      tooltipExtras: getRiskComparisonTooltipExtras(
        isBaseline,
        isOverall,
        well.operationalRisk,
        well.interventionRisk,
        well.combinedRisk,
        well?.lof,
        riskUnitFormatter,
      ),
    };
  });

  const { selectedWellSeries, otherWellsSeries } = splitSelectedAndPeerWells(
    allWellsSeries,
    wellName,
    hierarchyId,
    wellNamesByHierarchy,
  );

  const remainingColors = theme.colors.slice(0, 2);

  return {
    series: [
      {
        data: allWellsSeries,
        type: "scatter",
        id: "all-wells-ranks",
        name: "All Other Wells in System",
        size: 4,
      },
      {
        data: otherWellsSeries,
        type: "scatter",
        id: "other-wells-ranks",
        name: "Other Wells in the Same Field as Selected Well",
        size: 8,
      },
      {
        data: selectedWellSeries,
        type: "scatter",
        id: "selected-well-rank",
        name: "Selected Well",
        size: 8,
      },
    ],
    referenceLines: buildReferenceLines(
      chartData.fieldMedian,
      chartData.systemMedian,
      theme,
    ),
    legendDomain: [
      "Selected Well",
      "Other Wells in the Same Field as Selected Well",
      "All Other Wells in System",
    ],
    theme: {
      ...theme,
      colors: [
        theme.colors[10],
        theme.colors[3],
        theme.colors[2],
        ...remainingColors,
      ],
    },
    legendColors: [
      theme.colors[2],
      theme.colors[3],
      theme.colors[10],
      ...remainingColors,
    ],
  };
};

export const buildMitigationRiskComparisonChartConfig = (
  {
    chartData,
    wellName,
    hierarchyId,
    wellNamesByHierarchy,
    hierarchies,
    theme = chartTheme,
  }: Omit<RiskComparisonConfigInput, "chartData"> & {
    chartData?: RiskManagementOverallComparisonChartQuery["riskManagementOverallComparisonChart"];
  },
  riskMitigationName: string,
  riskUnitFormatter?: RiskUnitFormatter,
): RiskComparisonChartConfig => {
  if (!chartData) {
    return {
      series: [],
      referenceLines: [],
      legendDomain: [],
      legendColors: [],
      theme,
    };
  }

  const {
    mitigationWellRank = [],
  }: {
    mitigationWellRank?: RiskComparisonWellRanks;
  } = chartData;

  const sortedMitigationWellRank = sortBy(mitigationWellRank, "rank");

  const allWellsSeries = sortedMitigationWellRank.map((well) => {
    const wellHierarchyId = wellNamesByHierarchy?.find((hierarchy) => {
      return hierarchy?.wellNames?.includes(well.wellName);
    })?.hierarchyId;

    const hierarchyName = findHierarchyName(
      wellHierarchyId ?? null,
      hierarchies ?? [],
    );

    return {
      x: well.rank,
      y: well.operationalRisk,
      label: `${well.wellName} (${hierarchyName})`,
      title: `Risk Rank - ${well.rank}${nthNumberFormatter(well.rank)}`,
      value: `${well.rank} of ${sortedMitigationWellRank.length}`,
      wellName: well.wellName,
      tooltipExtras: [
        {
          label: "Mitigation",
          value: "Baseline",
          labelClassName: TOOLTIP_SEMIBOLD,
        },
        {
          label: "Baseline Operational Risk",
          value:
            riskUnitFormatter?.(well.operationalRisk) ?? well.operationalRisk,
        },
        {
          label: "Baseline Risk Rank",
          value: `${well.rank} of ${mitigationWellRank.length}`,
        },
        {
          label: "Post-Mitigation Operational Risk",
          value: riskUnitFormatter?.(well.postRisk ?? 0) ?? well.postRisk ?? 0,
        },
        {
          label: "Post-Mitigation Risk Rank",
          value: `${well.postRiskRank ?? 0} of ${mitigationWellRank.length}`,
        },
      ],
    };
  });

  const mitigationSeries = mitigationWellRank
    ?.filter((datum) => datum.wellName === wellName)
    .map((well) => {
      const wellHierarchyId = wellNamesByHierarchy?.find((hierarchy) => {
        return hierarchy?.wellNames?.includes(well.wellName);
      })?.hierarchyId;

      const hierarchyName = findHierarchyName(
        wellHierarchyId ?? null,
        hierarchies ?? [],
      );
      return {
        x: well.postRiskRank ?? 0,
        y: well.postRisk ?? 0,
        label: `${well.wellName} (${hierarchyName})`,
        title: `Post-Mitigation Risk Rank - ${well.postRiskRank ?? 0}${nthNumberFormatter(well.postRiskRank ?? 0)}`,
        value: `${well.postRiskRank ?? 0} of ${mitigationWellRank.length}`,
        wellName: well.wellName,
        tooltipExtras: [
          {
            label: "Mitigation",
            value: riskMitigationName,
            labelClassName: TOOLTIP_SEMIBOLD,
          },
          {
            label: "Baseline Operational Risk",
            value:
              riskUnitFormatter?.(well.operationalRisk) ?? well.operationalRisk,
          },
          {
            label: "Baseline Risk Rank",
            value: `${well.rank} of ${mitigationWellRank.length}`,
          },
          {
            label: "Post-Mitigation Operational Risk",
            value:
              riskUnitFormatter?.(well.postRisk ?? 0) ?? well.postRisk ?? 0,
          },
          {
            label: "Post-Mitigation Risk Rank",
            value: `${well.postRiskRank} of ${mitigationWellRank.length}`,
          },
        ],
      };
    });

  const { selectedWellSeries, otherWellsSeries } = splitSelectedAndPeerWells(
    allWellsSeries,
    wellName,
    hierarchyId,
    wellNamesByHierarchy,
  );

  return {
    series: [
      {
        data: allWellsSeries,
        type: "scatter",
        id: "all-wells-ranks",
        name: "All Other Wells in System",
        size: 4,
      },
      {
        data: otherWellsSeries,
        type: "scatter",
        id: "other-wells-ranks",
        name: "Other Wells in the Same Field as Selected Well",
        size: 8,
      },
      {
        data: selectedWellSeries,
        type: "scatter",
        id: "selected-well-rank",
        name: "Selected Well",
        size: 12,
      },
      {
        data: mitigationSeries,
        type: "scatter",
        id: "post-mitigation-selected-well-rank",
        name: "Post-Mitigation Risk",
        size: 8,
      },
    ],
    referenceLines: buildReferenceLines(
      chartData.fieldMedian,
      chartData.systemMedian,
      theme,
    ),
    legendDomain: [
      "Selected Well",
      "Post-Mitigation Risk",
      "Other Wells in the Same Field as Selected Well",
      "All Other Wells in System",
    ],
    theme: {
      ...theme,
      colors: [
        theme.colors[10],
        theme.colors[3],
        theme.colors[2],
        theme.colors[0],
        theme.colors[1],
      ],
    },
    legendColors: [
      theme.colors[2],
      theme.colors[0],
      theme.colors[3],
      theme.colors[10],
      theme.colors[1],
    ],
  };
};

const getRiskComparisonTooltipExtras = (
  isBaseline: boolean,
  isOverall: boolean,
  operationalRisk: number,
  interventionRisk: number,
  combinedRisk: number,
  lof?: number | null,
  riskUnitFormatter?: RiskUnitFormatter,
) => {
  const operationalRiskValue =
    riskUnitFormatter?.(operationalRisk) ?? operationalRisk;
  const interventionRiskValue =
    riskUnitFormatter?.(interventionRisk) ?? interventionRisk;
  const combinedRiskValue = riskUnitFormatter?.(combinedRisk) ?? combinedRisk;

  if (isOverall) {
    return [
      {
        label: `Risk Modality`,
        value: isBaseline ? "Operational Risk" : "Intervention Risk",
        labelClassName: TOOLTIP_SEMIBOLD,
      },
      {
        label: isBaseline
          ? "Overall Operational Risk"
          : "Overall Intervention Risk",
        value: isBaseline ? operationalRiskValue : interventionRiskValue,
      },
      {
        label: "Overall Combined Risk",
        value: combinedRiskValue,
      },
    ];
  }
  return [
    {
      label: `Risk Modality`,
      value: isBaseline ? "Operational Risk" : "Intervention Risk",
      labelClassName: TOOLTIP_SEMIBOLD,
    },
    {
      label: "LOF (Major Event)",
      value: storageFormatters.scientificNotation(lof ?? 0),
    },
    {
      label: isBaseline
        ? "Total Major Event Risk (Operational)"
        : "Total Major Event Risk (Intervention)",
      value: isBaseline ? operationalRiskValue : interventionRiskValue,
    },
    {
      label: "Total Major Event Risk (Combined)",
      value: combinedRiskValue,
    },
  ];
};
